import { useMemo, type ReactNode } from 'react';

import { CommandLifecycle } from '@coinweb/webapp-library';
import { v4 } from 'uuid';

import { useCoinwebWalletConnect } from '../hooks/useCoinwebWalletConnect';

import { CoinwebQrDialog } from './CoinwebQrDialog';
import { CoinwebContext } from './context';

interface CoinwebProviderProps {
  children: ReactNode;
  renderQrDialog?: boolean;
}

const INITIAL_MESSAGE = {
  id: v4(),
  type: CommandLifecycle.Pending,
  message: 'QR code scan pending...',
};

export const CoinwebProvider = ({ children, renderQrDialog = true }: CoinwebProviderProps): React.ReactNode => {
  const { address, isConnected, connect, disconnect, isConnecting, qrJson, message, setQrJson } =
    useCoinwebWalletConnect();

  const qrSection = useMemo(() => {
    if (!renderQrDialog || !qrJson) {
      return null;
    }
    return (
      <CoinwebQrDialog
        title="Connect your Coinweb wallet"
        description="Scan the QR code with your Coinweb wallet and confirm the transaction in order to connect your wallet."
        key={qrJson}
        message={message ?? INITIAL_MESSAGE}
        qrJsonString={qrJson}
        onClose={() => setQrJson(null)}
      />
    );
  }, [qrJson, message, renderQrDialog]);

  return (
    <CoinwebContext.Provider
      value={{
        address,
        isConnected,
        connect,
        disconnect,
        isConnecting,
      }}
    >
      {children}
      {qrSection}
    </CoinwebContext.Provider>
  );
};
